// ============================================================================
// VIDEO BLOB STORE - Binary video storage separated from project records
// ----------------------------------------------------------------------------
// Mirrors photo-blob-store.js. Videos imported in Stage 1 used to only exist
// as in-memory blob URLs (`URL.createObjectURL(file)`) which die on reload.
// This store persists the actual video Blob in IndexedDB and mints fresh
// Object URLs for playback when needed.
// ============================================================================

import db from './db';
import { FOLDER_AUDIO_BLOB_KIND } from './folder-audio-receipt';
import {
    omitCanonicalBlobFields,
    requireCanonicalProjectUuid,
} from './project-media-write-boundary';

// Object URL management.
// Track active object URLs for cleanup. getVideoUrl intentionally returns a
// fresh URL every time because some legacy callers still revoke URLs directly;
// returning a cached, already-revoked URL breaks later previews in the session.
const activeUrls = new Map(); // blobId -> Set<objectURL>
// Reverse index used by caller-owned cleanup paths. Stage 1 and the shared
// VideoPreviewPlayer revoke by URL; without removing that exact URL from the
// blobId set, this module retains one string per preview for the life of the
// tab and later re-revokes already-dead URLs.
const blobIdByUrl = new Map<string, unknown>();
// Explicit project deletion retires its UUID for the rest of the browser
// session. The tombstone prevents a stale IndexedDB read that began before the
// delete from minting an object URL after the row and prior URLs were swept.
// Transient rollback cleanup may opt out because that project can be retried.
const deletedProjectIds = new Set<string>();

export interface DeleteProjectVideoBlobOptions {
    retireProject?: boolean;
}

/**
 * Store a video Blob in the dedicated video_blobs table.
 *
 * @param {Blob|File} blob - The video Blob (File extends Blob)
 * @param {string} projectId - Associated project ID
 * @param {Object} [metadata] - Optional metadata (duration, width, height, etc.)
 * @returns {Promise<number>} The auto-generated blob ID
 */
export async function storeVideoBlob(blob: unknown, projectId: string, metadata: Record<string, unknown> = {}): Promise<number> {
    if (!(blob instanceof Blob)) {
        throw new Error('storeVideoBlob requires a Blob instance');
    }
    const canonicalProjectId = requireCanonicalProjectUuid(projectId, 'storeVideoBlob');
    const id = await (db as any).video_blobs.add({
        ...omitCanonicalBlobFields(metadata),
        blob,
        projectId: canonicalProjectId,
        sizeBytes: blob.size,
        mimeType: blob.type || 'video/mp4',
        storedAt: new Date().toISOString(),
    });
    return id;
}

/**
 * Retrieve a video Blob by its ID.
 *
 * @param {number} blobId
 * @param {string|null} [expectedProjectId] - When given, refuse blobs another project owns
 * @returns {Promise<Blob|null>}
 */
export async function getVideoBlob(blobId: number, expectedProjectId: string | null = null): Promise<Blob | null> {
    if (!blobId) return null;
    const expectedProjectKey = expectedProjectId == null ? null : String(expectedProjectId);
    if (expectedProjectKey != null && deletedProjectIds.has(expectedProjectKey)) return null;
    const record = await (db as any).video_blobs.get(blobId);
    if (!record) return null;
    // INTEGRITY GUARD (v5.46.73, mirrors getPhotoBlob): video_blobs `id` is a
    // GLOBAL Dexie auto-increment, so a video whose metadata carries another
    // project's videoBlobId (e.g. after a local-DB reset re-seeded the
    // counter) would otherwise resolve the WRONG project's footage. When the
    // caller names the owning project, refuse to resolve a blob it does not
    // own.
    if (expectedProjectId != null && String(record.projectId ?? '') !== String(expectedProjectId)) {
        console.warn(`[video-blob-store] Refused cross-project blob ${blobId} (owner=${record.projectId}, requested=${expectedProjectId})`);
        return null;
    }
    const ownerProjectKey = record.projectId == null ? expectedProjectKey : String(record.projectId);
    // Re-check after the IndexedDB await: an explicit delete can complete while
    // this read still holds a stale row result.
    if ((expectedProjectKey != null && deletedProjectIds.has(expectedProjectKey))
        || (ownerProjectKey != null && deletedProjectIds.has(ownerProjectKey))) return null;
    return record.blob || null;
}

/**
 * Clone browser-local video blobs for a duplicated project.
 *
 * A videoBlobId is owned by exactly one project. The duplicate receives one
 * fresh target-owned row per distinct source id, while cloud/file references
 * remain untouched. Source-bound Blob URLs are removed so the duplicate mints
 * its own playable URL from the remapped row after project switching/reload.
 */
export async function cloneProjectVideoBlobs(
    videos: unknown,
    sourceProjectId: string,
    targetProjectId: string,
): Promise<any[]> {
    if (!Array.isArray(videos) || !sourceProjectId || !targetProjectId) {
        return Array.isArray(videos) ? videos : [];
    }
    const clonedIds = new Map<string, number | null>();
    const createdTargetBlobIds: number[] = [];
    const out: any[] = [];
    try {
        for (const video of videos) {
            if (!video || typeof video !== 'object') {
                out.push(video);
                continue;
            }
            const sourceBlobId = (video as any).videoBlobId;
            let targetBlobId: number | null = null;
            if (sourceBlobId) {
                const cloneKey = String(sourceBlobId);
                if (clonedIds.has(cloneKey)) {
                    targetBlobId = clonedIds.get(cloneKey) ?? null;
                } else {
                    const sourceBlob = await getVideoBlob(sourceBlobId, sourceProjectId);
                    if (sourceBlob) {
                        targetBlobId = await storeVideoBlob(sourceBlob, targetProjectId, {
                            name: (video as any).name || (video as any).originalName,
                            type: sourceBlob.type || (video as any).type || (video as any).mimeType,
                            duration: (video as any).duration,
                            width: (video as any).width,
                            height: (video as any).height,
                        });
                        createdTargetBlobIds.push(targetBlobId);
                    }
                    clonedIds.set(cloneKey, targetBlobId);
                }
            }

            const next = { ...(video as any) };
            if (sourceBlobId) {
                if (targetBlobId) next.videoBlobId = targetBlobId;
                else {
                    const hasPortableCloudReceipt = !!(
                        (video as any).s3Key
                        || (video as any).fileMetadataId
                        || (video as any).cloudPlayback?.s3Key
                        || (video as any).cloudPlayback?.fileMetadataId
                    );
                    if (!hasPortableCloudReceipt) {
                        const error: any = new Error(
                            `Cannot duplicate video "${(video as any).name || (video as any).originalName || (video as any).id || sourceBlobId}": `
                            + `its only browser-local blob ${sourceBlobId} is unavailable.`,
                        );
                        error.code = 'DUPLICATE_LOCAL_MEDIA_UNAVAILABLE';
                        error.mediaKind = 'video';
                        error.blobId = sourceBlobId;
                        throw error;
                    }
                    delete next.videoBlobId;
                }
            }
            if (targetBlobId) {
                // The target-owned IndexedDB row is now the durable original.
                // Avoid retaining a second raw File/Blob/byte buffer in React and
                // the Dexie project envelope; derived worker proxies/keyframes and
                // portable cloud references remain available.
                for (const key of [
                    'file', 'blob', 'bytes', 'data', 'dataUrl',
                    'handle', 'fileHandle', 'directoryHandle',
                ]) delete next[key];
            }
            if (typeof next.url === 'string' && next.url.startsWith('blob:')) {
                delete next.url;
            }
            out.push(next);
        }
        return out;
    } catch (error) {
        // Do not report a successful duplicate with only some of its source
        // videos. App.jsx performs the wider photo/document/video rollback.
        await Promise.allSettled(createdTargetBlobIds.map((blobId) => (
            (db as any).video_blobs.delete(blobId)
        )));
        throw error;
    }
}

/**
 * Owners that mean "no project had an id yet when this blob was stored", as
 * opposed to a REAL project id. Stage 1 stamps 'pending' when videos are added
 * before the first save mints the project id (Stage1DataEntry.jsx ~2912); the
 * photo store has the same class of hole with `null` and 'unknown'. Kept as a
 * shared list so the claim below and any future stamping site agree.
 */
const SENTINEL_OWNERS = new Set(['pending', 'unknown', '', 'null', 'undefined']);

function isSentinelOwner(owner: unknown): boolean {
    return owner == null || SENTINEL_OWNERS.has(String(owner));
}

export interface VideoBlobReferenceInspection {
    ownedIds: number[];
    sentinelIds: number[];
    foreignRefs: Array<{ id: number; ownerProjectId: string }>;
    missingIds: number[];
}

/**
 * Classify browser-local video blob references without mutating ownership.
 *
 * `videoBlobId` is meaningful only inside one browser profile's IndexedDB.
 * A Project loaded from the backend or another Chrome profile can therefore
 * carry an id that is simply absent here. That is not a cross-project breach;
 * it is an unavailable device-local cache reference and should fall back to
 * the cloud derivative or source-folder path. A present row owned by a real,
 * different Project remains a genuine integrity mismatch.
 */
export async function inspectVideoBlobReferences(
    blobIds: Array<number | null | undefined>,
    projectId: string,
): Promise<VideoBlobReferenceInspection> {
    const result: VideoBlobReferenceInspection = {
        ownedIds: [],
        sentinelIds: [],
        foreignRefs: [],
        missingIds: [],
    };
    if (!projectId || isSentinelOwner(projectId) || !Array.isArray(blobIds)) return result;

    const uniqueIds = [...new Set(blobIds.filter((id): id is number => (
        typeof id === 'number' && Number.isFinite(id)
    )))];
    for (const id of uniqueIds) {
        try {
            const record = await (db as any).video_blobs.get(id);
            if (!record) {
                result.missingIds.push(id);
            } else if (String(record.projectId ?? '') === String(projectId)) {
                result.ownedIds.push(id);
            } else if (isSentinelOwner(record.projectId)) {
                result.sentinelIds.push(id);
            } else {
                result.foreignRefs.push({ id, ownerProjectId: String(record.projectId) });
            }
        } catch {
            // A failed local read cannot establish a foreign owner. Treat it as
            // unavailable so the caller fails over to a portable source.
            result.missingIds.push(id);
        }
    }
    return result;
}

/** Legacy compatibility API; unsafe integer-only sentinel adoption is disabled. */
export async function claimSentinelVideoBlobs(
    _blobIds: Array<number | null | undefined>,
    _projectId: string,
): Promise<number[]> {
    return [];
}

/**
 * Get a displayable Object URL for a video.
 * Returns a fresh Object URL. Call revokeVideoUrl() when the player unmounts
 * if the caller does not already own/revoke the returned URL.
 *
 * @param {number} blobId
 * @param {string|null} [expectedProjectId] - When given, refuse blobs another project owns
 * @returns {Promise<string|null>} Object URL or null if blob not found
 */
export async function getVideoUrl(blobId: number, expectedProjectId: string | null = null): Promise<string | null> {
    if (!blobId) return null;
    const blob = await getVideoBlob(blobId, expectedProjectId);
    if (!blob) return null;
    const url = URL.createObjectURL(blob);
    const urls = activeUrls.get(blobId) || new Set();
    urls.add(url);
    activeUrls.set(blobId, urls);
    blobIdByUrl.set(url, blobId);
    return url;
}

/**
 * Revoke one Object URL and remove only its matching bookkeeping entry.
 * Shared project ownership is resolved by the caller's registry before this
 * final boundary is invoked; this function never sweeps sibling URLs for the
 * same video blob.
 */
export function revokeVideoObjectUrl(url: unknown): boolean {
    if (typeof url !== 'string' || !url.startsWith('blob:')) return false;
    const blobId = blobIdByUrl.get(url);
    if (blobId !== undefined) {
        const urls = activeUrls.get(blobId);
        urls?.delete(url);
        if (urls?.size === 0) activeUrls.delete(blobId);
        blobIdByUrl.delete(url);
    }
    try { URL.revokeObjectURL(url); } catch { /* browser cleanup is best-effort */ }
    return true;
}

/**
 * Revoke all tracked Object URLs for a video blob.
 *
 * @param {number} blobId
 */
export function revokeVideoUrl(blobId: number): void {
    const urls = activeUrls.get(blobId);
    if (!urls) return;
    for (const url of [...urls]) revokeVideoObjectUrl(url);
    activeUrls.delete(blobId);
}

/**
 * Revoke all active video Object URLs. Call on project switch or page unload.
 */
export function revokeAllVideoUrls() {
    for (const url of [...blobIdByUrl.keys()]) revokeVideoObjectUrl(url);
    activeUrls.clear();
    blobIdByUrl.clear();
}

/**
 * Delete all video Blobs for a project.
 *
 * @param {string} projectId
 * @returns {Promise<number>} Number of Blobs deleted
 */
export async function deleteProjectVideoBlobs(
    projectId: string,
    { retireProject = true }: DeleteProjectVideoBlobOptions = {},
): Promise<number> {
    if (!projectId) return 0;
    if (retireProject) deletedProjectIds.add(String(projectId));
    const projectBlobs = await (db as any).video_blobs.where('projectId').equals(projectId).toArray();
    const blobIds = new Set(projectBlobs.map((r: any) => r.id));
    await (db as any).video_blobs.where('projectId').equals(projectId).delete();
    for (const id of blobIds) {
        revokeVideoUrl(id as number);
    }
    return projectBlobs.length;
}

/** Delete one video blob, refusing to cross project ownership boundaries. */
export async function deleteVideoBlob(blobId: number, expectedProjectId: string | null = null): Promise<boolean> {
    if (!blobId) return false;
    const record = await (db as any).video_blobs.get(blobId);
    if (!record) return false;
    if (expectedProjectId != null && String(record.projectId ?? '') !== String(expectedProjectId)) {
        console.warn(`[video-blob-store] Refused cross-project delete for blob ${blobId} (owner=${record.projectId}, requested=${expectedProjectId})`);
        return false;
    }
    await (db as any).video_blobs.delete(blobId);
    revokeVideoUrl(blobId);
    return true;
}

/**
 * Get all video Blob IDs for a project (without loading the binary data).
 * Mirrors getProjectBlobIds in photo-blob-store — used by the load-time
 * ownership sanitise to strip videoBlobId refs the project does not own.
 *
 * @param {string} projectId
 * @returns {Promise<Array<{id: number, sizeBytes: number, mimeType: string, storedAt: string}>>}
 */
export async function getProjectVideoBlobIds(projectId: string): Promise<Array<{ id: number; sizeBytes: number; mimeType: string; storedAt: string }>> {
    if (!projectId) return [];
    const records: any[] = await (db as any).video_blobs.where('projectId').equals(projectId).toArray();
    return records.map(r => ({
        id: r.id,
        sizeBytes: r.sizeBytes,
        mimeType: r.mimeType,
        storedAt: r.storedAt,
    }));
}

/**
 * Find the one browser-local blob this project owns for a given video row.
 *
 * A video row can lose its `videoBlobId` while the bytes are still here (a
 * partial project save used to mirror a locator-free server row over the local
 * one). Re-binding must never guess: the blob has to be owned by exactly this
 * project, match the recorded byte size exactly, and agree on the file name or
 * relative path wherever the blob recorded one. If that does not single out
 * exactly one blob, nothing is returned and the caller reports the video as
 * unavailable. No cross-project search and no filename-only match.
 */
export async function findOwnedVideoBlobId(
    projectId: string | null | undefined,
    video: { size?: unknown; name?: unknown; originalName?: unknown; relativePath?: unknown } | null | undefined,
): Promise<number | null> {
    const owner = projectId == null ? '' : String(projectId);
    const size = Number(video?.size);
    if (!owner || !Number.isFinite(size) || size <= 0) return null;
    if (deletedProjectIds.has(owner)) return null;
    const lower = (value: unknown) => String(value ?? '').trim().toLowerCase();
    const wantedNames = new Set([lower(video?.originalName), lower(video?.name)].filter(Boolean));
    const wantedPath = lower(video?.relativePath);
    if (wantedNames.size === 0 && !wantedPath) return null;
    const records: any[] = await (db as any).video_blobs.where('projectId').equals(owner).toArray();
    const matches = records.filter((record) => {
        // A folder recording waiting for its run is never a video's bytes.
        if (record?.kind === FOLDER_AUDIO_BLOB_KIND) return false;
        if (Number(record?.sizeBytes) !== size) return false;
        const recordedNames = [lower(record?.originalName), lower(record?.name)].filter(Boolean);
        const recordedPath = lower(record?.relativePath);
        // A blob that recorded no name or path cannot be tied to one specific
        // video row, so it is never re-bound.
        if (recordedNames.length === 0 && !recordedPath) return false;
        if (recordedNames.length > 0 && !recordedNames.some(name => wantedNames.has(name))) return false;
        if (recordedPath && wantedPath && recordedPath !== wantedPath
            && !recordedPath.endsWith(`/${wantedPath}`) && !wantedPath.endsWith(`/${recordedPath}`)) return false;
        return true;
    });
    return matches.length === 1 && typeof matches[0].id === 'number' ? matches[0].id : null;
}

/**
 * The folder recordings this project's run has still to read: the audio files
 * a folder import stored (preprocessed-row-builder), with their bytes. A Stage
 * 1 drop transcribes on the spot and stores none.
 */
export async function getProjectFolderAudioBlobs(projectId: string): Promise<any[]> {
    if (!projectId || deletedProjectIds.has(String(projectId))) return [];
    const records: any[] = await (db as any).video_blobs.where('projectId').equals(projectId).toArray();
    return records.filter(record => record?.kind === FOLDER_AUDIO_BLOB_KIND);
}

/**
 * Get total storage used by all video Blobs (in bytes).
 *
 * @returns {Promise<{count: number, totalBytes: number}>}
 */
export async function getStorageStats(): Promise<{ count: number; totalBytes: number }> {
    const all = await (db as any).video_blobs.toArray();
    return {
        count: all.length,
        totalBytes: all.reduce((sum: number, r: any) => sum + (r.sizeBytes || 0), 0),
    };
}
